import React, { useState, useEffect, useMemo } from "react";
import {
  MdSearch,
  MdFilterList,
  MdLocationOn,
  MdAccessTime,
  MdLocalHospital,
  MdPhone,
  MdMap,
  MdList,
  MdDirections,
  MdInfo,
  MdShareLocation,
} from "react-icons/md";
import {
  FaFilter,
  FaWalking,
  FaCar,
  FaWheelchair,
  FaStethoscope,
} from "react-icons/fa";
import Card from "components/card";
import ClinicMap from "../components/ClinicMap";
import { useToast } from "hooks/useToast";
import Modal from "components/modal/Modal";
import BookingModal from "../components/BookingModal";
import { useProvider } from "hooks/useProvider";

const FindClinic = () => {
  const [viewMode, setViewMode] = useState("map");
  const [selectedFilters, setSelectedFilters] = useState({
    freeServices: true,
    childHealth: false,
    openNow: false,
    vaccinations: false,
    emergency: false,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const { showToast } = useToast();

  // Modal states
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [directionsModalOpen, setDirectionsModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [smsConfirmModalOpen, setSmsConfirmModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [bookingData, setBookingData] = useState(null);

  // Mock clinic data - In production, this would come from an API
  const { clinics, loading, error, getClinics } = useProvider();

  useEffect(() => {
    // Only fetch if clinics don't already exist
    if (!clinics || clinics.length === 0) {
      getClinics();
    }
  }, []);

  const formatClinicData = (clinic) => {
    const getClinicStatus = () => {
      const now = new Date();
      const currentDay = now
        .toLocaleDateString("en-US", { weekday: "long" })
        .toLowerCase();
      const currentTime = now.getHours() * 100 + now.getMinutes();
      const hours = clinic.operating_hours?.[currentDay];

      if (!hours || hours === "closed") return "Closed";

      const openTime = parseInt(hours.open?.replace(":", "") || "0");
      const closeTime = parseInt(hours.close?.replace(":", "") || "2400");

      if (currentTime >= openTime && currentTime <= closeTime) {
        return "Open Now";
      } else {
        return "Closed";
      }
    };

    const formatOperatingHours = () => {
      if (!clinic.operating_hours) return "Mon-Fri: 8am-6pm";

      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      const hoursArray = days
        .map((day) => {
          const hours = clinic.operating_hours[day];
          if (hours === "closed") return null;

          const dayName = day.charAt(0).toUpperCase() + day.slice(1);
          const openTime = hours?.open || "N/A";
          const closeTime = hours?.close || "N/A";

          return `${dayName}: ${openTime}-${closeTime}`;
        })
        .filter(Boolean);

      return hoursArray.length > 0
        ? hoursArray.join(", ")
        : "Hours not available";
    };

    return {
      id: clinic.id,
      name: clinic.clinic_name || "Clinic",
      distance: clinic.distance || "N/A",
      address: `${clinic.physical_address || ""}, ${clinic.city || ""}, ${
        clinic.province || ""
      }`.trim(),
      status: getClinicStatus(),
      services: clinic.services || [],
      specialties: clinic.specialties || [],
      waitTime: clinic.average_wait_time_minutes
        ? `${clinic.average_wait_time_minutes} min`
        : "N/A",
      hours: formatOperatingHours(),
      phone: clinic.primary_phone || "N/A",
      rating: clinic.rating || 0,
      reviewCount: clinic.review_count || 0,
      features: clinic.facilities || [],
      languages: clinic.languages_spoken || [],
      acceptsMedicalAid: clinic.accepts_medical_aid,
      medicalAidProviders: clinic.medical_aid_providers || [],
      paymentMethods: clinic.payment_methods || [],
      email: clinic.email || "",
      website: clinic.website || "",
      description: clinic.description || "",
      bedCount: clinic.bed_count || 0,
      yearEstablished: clinic.year_established || "N/A",
      isVerified: clinic.is_verified || false,
      latitude: clinic.latitude,
      longitude: clinic.longitude,

      coordinates: {
        lat: clinic.latitude,
        lng: clinic.longitude,
      },
    };
  };

  useEffect(() => {
    // Get user's location
    if (navigator.geolocation) {
      setLocationModalOpen(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setLocationModalOpen(false);
          showToast("Location found successfully", "success");
        },
        (error) => {
          setLocationModalOpen(false);
          if (error.code === error.PERMISSION_DENIED) {
            showToast(
              "Location permission denied. Using default location.",
              "warning"
            );
            setUserLocation({ lat: -26.2041, lng: 28.0473 }); // Default to Johannesburg
          } else {
            showToast("Error getting location", "error");
          }
        }
      );
    } else {
      showToast("Geolocation not supported by your browser", "error");
      setUserLocation({ lat: -26.2041, lng: 28.0473 });
    }
  }, []);

  const formattedClinics = useMemo(() => {
    return clinics ? clinics.map(formatClinicData) : [];
  }, [clinics]);

  const filteredClinics = formattedClinics.filter((clinic) => {
    // Search filter
    if (
      searchQuery &&
      !clinic.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !clinic.address.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    // Service filters
    if (selectedFilters.freeServices && !clinic.freeServices) return false;
    if (selectedFilters.childHealth && !clinic.childHealth) return false;
    if (selectedFilters.openNow && clinic.status === "Closed") return false;
    if (
      selectedFilters.vaccinations &&
      !clinic.services.includes("Vaccinations")
    )
      return false;
    if (selectedFilters.emergency && !clinic.emergency) return false;

    return true;
  });

  const handleBookAppointment = (clinic) => {
    setSelectedClinic(clinic);
    setBookModalOpen(true);
  };

  const confirmBooking = () => {
    setBookModalOpen(false);
    showToast(`Appointment booked at ${selectedClinic.name}`, "success");
    // Navigate to booking page
    window.location.href = `/patient/book-appointment?clinic=${selectedClinic.id}`;
  };

  const handleGetDirections = (clinic) => {
    setSelectedClinic(clinic);
    setDirectionsModalOpen(true);
  };

  const confirmDirections = (transport) => {
    setDirectionsModalOpen(false);
    showToast(
      `Opening ${transport} directions to ${selectedClinic.name}`,
      "info"
    );

    const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedClinic.coordinates.lat},${selectedClinic.coordinates.lng}&travelmode=${transport}`;
    window.open(url, "_blank");
  };

  const handleShareClinic = (clinic) => {
    setSelectedClinic(clinic);
    setShareModalOpen(true);
  };

  const confirmShare = (method) => {
    setShareModalOpen(false);
    showToast(`Clinic shared via ${method}`, "success");
  };

  const handleUpdateLocation = () => {
    setLocationModalOpen(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationModalOpen(false);
        showToast("Location updated successfully", "success");
      },
      () => {
        setLocationModalOpen(false);
        showToast("Could not update location", "error");
      }
    );
  };

  const FilterButton = ({ icon, label, filterKey }) => (
    <button
      onClick={() =>
        setSelectedFilters((prev) => ({
          ...prev,
          [filterKey]: !prev[filterKey],
        }))
      }
      className={`flex items-center rounded-full px-4 py-2 text-sm font-medium transition-colors ${
        selectedFilters[filterKey]
          ? "bg-brand-500 text-white"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300"
      }`}
    >
      {icon}
      <span className="ml-2">{label}</span>
    </button>
  );

  return (
    <div className="h-full">
      {/* Modals */}
      {/* Booking Modal  */}
      <div className="z-50">
        {bookModalOpen && selectedClinic && (
          <BookingModal
            isOpen={bookModalOpen}
            onClose={() => setBookModalOpen(false)}
            clinic={selectedClinic}
            type="quick"
            onConfirmBooking={(bookingData) => {
              setBookModalOpen(false);
              setBookingData(bookingData);
              setSelectedDate(
                bookingData.date || new Date().toISOString().split("T")[0]
              );
              setSelectedTime(bookingData.time || "09:00 AM");
              showToast(
                `Quick appointment booked at ${selectedClinic.name}`,
                "success"
              );
              setTimeout(() => {
                setSmsConfirmModalOpen(true);
              }, 500);
            }}
          />
        )}

        {/* SMS Confirmation Modal  */}
        <Modal
          isOpen={smsConfirmModalOpen}
          onClose={() => setSmsConfirmModalOpen(false)}
          title="SMS Appointment Confirmation"
          size="md"
        >
          <div className="space-y-6">
            <div className="text-center">
              <MdPhone className="mx-auto mb-4 h-12 w-12 text-brand-500" />
              <h4 className="mb-2 text-xl font-bold text-navy-700 dark:text-white">
                Appointment Booked!
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                An SMS confirmation has been sent to your phone.
              </p>
            </div>

            <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
              <div className="flex items-start">
                <MdInfo className="mr-2 mt-0.5 h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    Confirmation Details:
                  </p>
                  <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                    Date: {selectedDate || "Tomorrow"}
                    <br />
                    Time: {selectedTime || "09:00 AM"}
                    <br />
                    Clinic: {selectedClinic?.name}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                What's next?
              </h5>
              <div className="space-y-2">
                <div className="flex items-center rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                  <span className="mr-3 text-blue-600">1.</span>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    Arrive 10 minutes before your appointment
                  </div>
                </div>
                <div className="flex items-center rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                  <span className="mr-3 text-green-600">2.</span>
                  <div className="text-sm text-green-700 dark:text-green-300">
                    Bring ID and medical aid card
                  </div>
                </div>
                <div className="flex items-center rounded-lg bg-purple-50 p-3 dark:bg-purple-900/20">
                  <span className="mr-3 text-purple-600">3.</span>
                  <div className="text-sm text-purple-700 dark:text-purple-300">
                    Check SMS for cancellation instructions
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setSmsConfirmModalOpen(false);
                  setSuccessModalOpen(true); // Open success modal
                }}
                className="rounded-lg border border-gray-300 px-6 py-2 font-medium hover:bg-gray-50 dark:border-gray-600"
              >
                View Details
              </button>
              <button
                onClick={() => {
                  setSmsConfirmModalOpen(false);
                  showToast("Appointment confirmed!", "success");
                }}
                className="rounded-lg bg-brand-500 px-6 py-2 font-medium text-white hover:bg-brand-600"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>

        {/* Success Confirmation Modal */}
        <Modal
          isOpen={successModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          title="Appointment Confirmed"
          size="md"
        >
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <MdAccessTime className="h-8 w-8 text-green-600 dark:text-green-300" />
              </div>
              <h4 className="mb-2 text-xl font-bold text-navy-700 dark:text-white">
                Appointment Booked Successfully!
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                Your appointment has been confirmed and added to your calendar.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <span className="text-gray-700 dark:text-gray-300">
                  Clinic:
                </span>
                <span className="font-medium">{selectedClinic?.name}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <span className="text-gray-700 dark:text-gray-300">Date:</span>
                <span className="font-medium">
                  {selectedDate || "Tomorrow"}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <span className="text-gray-700 dark:text-gray-300">Time:</span>
                <span className="font-medium">
                  {selectedTime || "09:00 AM"}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <span className="text-gray-700 dark:text-gray-300">
                  Confirmation:
                </span>
                <span className="font-medium text-green-600">SMS Sent ✓</span>
              </div>
            </div>

            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <div className="flex items-start">
                <MdInfo className="mr-2 mt-0.5 h-5 w-5 text-blue-500" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  To cancel or reschedule, reply "CANCEL" to the SMS or call the
                  clinic directly.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setSuccessModalOpen(false);
                  showToast("Added to your appointments", "info");
                  window.location.href = "/patient/appointments";
                }}
                className="rounded-lg bg-brand-500 px-6 py-2 font-medium text-white hover:bg-brand-600"
              >
                View My Appointments
              </button>
            </div>
          </div>
        </Modal>

        {/* Directions Modal */}
        <Modal
          isOpen={directionsModalOpen}
          onClose={() => setDirectionsModalOpen(false)}
          title="Get Directions"
          size="md"
        >
          {selectedClinic && (
            <div className="space-y-6">
              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <h4 className="font-bold text-navy-700 dark:text-white">
                  {selectedClinic.name}
                </h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {selectedClinic.distance} away
                </p>
              </div>

              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Choose transportation:
                </h5>
                <button
                  onClick={() => confirmDirections("walking")}
                  className="flex w-full items-center rounded-lg border border-gray-200 p-3 hover:border-brand-500 hover:bg-brand-50 dark:border-gray-700"
                >
                  <FaWalking className="mr-3 h-5 w-5 text-blue-500" />
                  <div className="flex-1 text-left">
                    <div className="font-medium">Walking</div>
                    <div className="text-sm text-gray-500">
                      ~{Math.round(parseFloat(selectedClinic.distance) * 15)}{" "}
                      minutes
                    </div>
                  </div>
                  <span>→</span>
                </button>

                <button
                  onClick={() => confirmDirections("driving")}
                  className="flex w-full items-center rounded-lg border border-gray-200 p-3 hover:border-brand-500 hover:bg-brand-50 dark:border-gray-700"
                >
                  <FaCar className="mr-3 h-5 w-5 text-green-500" />
                  <div className="flex-1 text-left">
                    <div className="font-medium">Driving</div>
                    <div className="text-sm text-gray-500">
                      ~{Math.round(parseFloat(selectedClinic.distance) * 3)}{" "}
                      minutes
                    </div>
                  </div>
                  <span>→</span>
                </button>

                <button
                  onClick={() => confirmDirections("transit")}
                  className="flex w-full items-center rounded-lg border border-gray-200 p-3 hover:border-brand-500 hover:bg-brand-50 dark:border-gray-700"
                >
                  <MdDirections className="mr-3 h-5 w-5 text-purple-500" />
                  <div className="flex-1 text-left">
                    <div className="font-medium">Public Transport</div>
                    <div className="text-sm text-gray-500">Check schedules</div>
                  </div>
                  <span>→</span>
                </button>
              </div>

              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <div className="flex items-start">
                  <MdInfo className="mr-2 mt-0.5 h-5 w-5 text-blue-600" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Directions will open in Google Maps. Make sure you have the
                    app installed.
                  </p>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Location Permission Modal */}
        <Modal
          isOpen={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          title="Location Access Required"
          size="md"
        >
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                <MdShareLocation className="h-8 w-8 text-blue-600 dark:text-blue-300" />
              </div>
              <h4 className="mb-2 text-xl font-bold text-navy-700 dark:text-white">
                Allow Location Access
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                We need your location to show clinics near you and calculate
                distances.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                <MdLocationOn className="mr-3 h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-medium text-blue-800 dark:text-blue-300">
                    Find Nearest Clinics
                  </div>
                  <div className="text-sm text-blue-600 dark:text-blue-400">
                    Show clinics within your area
                  </div>
                </div>
              </div>

              <div className="flex items-center rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                <MdDirections className="mr-3 h-5 w-5 text-green-600" />
                <div>
                  <div className="font-medium text-green-800 dark:text-green-300">
                    Get Accurate Directions
                  </div>
                  <div className="text-sm text-green-600 dark:text-green-400">
                    Calculate travel time and routes
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
              <div className="flex items-start">
                <MdInfo className="mr-2 mt-0.5 h-5 w-5 text-yellow-600" />
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Your location data is only used to find nearby clinics and is
                  not stored or shared.
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setLocationModalOpen(false)}
                className="rounded-lg border border-gray-300 px-6 py-3 font-medium hover:bg-gray-50 dark:border-gray-600"
              >
                Use Default Location
              </button>
            </div>
          </div>
        </Modal>

        {/* Filter Modal */}
        <Modal
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          title="Filter Clinics"
          size="md"
        >
          <div className="space-y-6">
            <div>
              <h5 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                Services
              </h5>
              <div className="space-y-2">
                {Object.entries(selectedFilters).map(([key, value]) => {
                  const labels = {
                    freeServices: "Free Services",
                    childHealth: "Child Health",
                    openNow: "Open Now",
                    vaccinations: "Vaccinations",
                    emergency: "Emergency",
                  };

                  return (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) =>
                          setSelectedFilters((prev) => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                        className="mr-3 h-4 w-4 rounded border-gray-300"
                      />
                      <span>{labels[key]}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <h5 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                Distance
              </h5>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="distance"
                    className="mr-3"
                    defaultChecked
                  />
                  <span>Within 5 km</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="distance" className="mr-3" />
                  <span>Within 10 km</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="distance" className="mr-3" />
                  <span>Any distance</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setSelectedFilters({
                    freeServices: false,
                    childHealth: false,
                    openNow: false,
                    vaccinations: false,
                    emergency: false,
                  });
                  showToast("Filters cleared", "info");
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600"
              >
                Clear All
              </button>
              <button
                onClick={() => {
                  setFilterModalOpen(false);
                  showToast("Filters applied", "success");
                }}
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </Modal>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
          Find a Clinic Near You
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          Search for healthcare facilities by location, services, and
          availability
        </p>
      </div>

      {/* Search and Controls */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Search Bar */}
        <div className="lg:col-span-2">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by clinic name, address, or services..."
              className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-4 dark:border-gray-600 dark:bg-navy-800"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={handleUpdateLocation}
            className="flex items-center rounded-lg bg-blue-100 px-3 py-2 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300"
          >
            <MdLocationOn className="h-4 w-4" />
          </button>
          <button
            onClick={() => setFilterModalOpen(true)}
            className="flex items-center rounded-lg bg-gray-100 px-4 py-2 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300"
          >
            <MdFilterList className="mr-2 h-4 w-4" />
            Filters
          </button>
          <button
            onClick={() => setViewMode("map")}
            className={`flex items-center rounded-lg px-4 py-2 ${
              viewMode === "map"
                ? "bg-brand-500 text-white"
                : "bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300"
            }`}
          >
            <MdMap className="mr-2 h-4 w-4" />
            Map
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`flex items-center rounded-lg px-4 py-2 ${
              viewMode === "list"
                ? "bg-brand-500 text-white"
                : "bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300"
            }`}
          >
            <MdList className="mr-2 h-4 w-4" />
            List
          </button>
        </div>
      </div>

      {/* Filter Chips */}
      <div className="mb-6 flex flex-wrap gap-2">
        <FilterButton
          icon={<FaFilter className="h-4 w-4" />}
          label="Free Services"
          filterKey="freeServices"
        />
        <FilterButton
          icon={<MdLocalHospital className="h-4 w-4" />}
          label="Child Health"
          filterKey="childHealth"
        />
        <FilterButton
          icon={<MdAccessTime className="h-4 w-4" />}
          label="Open Now"
          filterKey="openNow"
        />
        <FilterButton
          icon={<FaWheelchair className="h-4 w-4" />}
          label="Vaccinations"
          filterKey="vaccinations"
        />
        <FilterButton
          icon={<FaStethoscope className="h-4 w-4" />}
          label="Emergency"
          filterKey="emergency"
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Map View */}
        {viewMode === "map" && (
          <div className="lg:col-span-2">
            <Card extra="p-0 overflow-hidden">
              <div className="h-[500px]">
                <ClinicMap
                  clinics={filteredClinics}
                  userLocation={userLocation}
                  onClinicSelect={setSelectedClinic}
                />
              </div>
            </Card>
          </div>
        )}

        {/* Clinic List */}
        <div
          className={`${
            viewMode === "map" ? "lg:col-span-1" : "lg:col-span-3"
          }`}
        >
          <div className="space-y-4">
            {filteredClinics.length === 0 ? (
              <Card extra="p-6 text-center">
                <MdLocalHospital className="mx-auto h-12 w-12 text-gray-400" />
                <h4 className="mt-4 text-lg font-bold text-navy-700 dark:text-white">
                  No clinics found
                </h4>
                <p className="mt-2 text-gray-600">
                  Try adjusting your filters or search terms
                </p>
                <button
                  onClick={() => {
                    setSelectedFilters({
                      freeServices: false,
                      childHealth: false,
                      openNow: false,
                      vaccinations: false,
                      emergency: false,
                    });
                    setSearchQuery("");
                    showToast("Filters cleared", "info");
                  }}
                  className="linear mt-4 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
                >
                  Clear All Filters
                </button>
              </Card>
            ) : (
              filteredClinics.map((clinic) => (
                <Card
                  key={clinic.id}
                  extra="p-4 hover:shadow-lg transition-shadow"
                >
                  <div className="space-y-3">
                    {/* Clinic Header */}
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <MdLocalHospital className="h-5 w-5 text-brand-500" />
                          <h5 className="font-bold text-navy-700 dark:text-white">
                            {clinic.name}
                          </h5>
                        </div>
                        <div className="mt-1 flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <MdLocationOn className="mr-2 h-4 w-4" />
                          {clinic.address}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-medium ${
                            clinic.status === "Open Now" ||
                            clinic.status === "24/7 Emergency"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {clinic.status}
                        </span>
                        <button
                          onClick={() => handleShareClinic(clinic)}
                          className="text-xs text-gray-500 hover:text-brand-500"
                        >
                          Share
                        </button>
                      </div>
                    </div>

                    {/* Clinic Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center text-sm">
                        <FaWalking className="mr-2 h-4 w-4 text-gray-400" />
                        <span>{clinic.distance} away</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <MdAccessTime className="mr-2 h-4 w-4 text-gray-400" />
                        <span>Wait: {clinic.waitTime}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <MdPhone className="mr-2 h-4 w-4 text-gray-400" />
                        <span>{clinic.phone}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Hours: </span>
                        <span>{clinic.hours}</span>
                      </div>
                    </div>

                    {/* Services & Languages */}
                    <div>
                      <div className="mb-2 flex flex-wrap gap-1">
                        {clinic.services.map((service, idx) => (
                          <span
                            key={idx}
                            className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {clinic.languages?.map((lang, idx) => (
                          <span
                            key={idx}
                            className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-navy-700 dark:text-gray-300"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleGetDirections(clinic)}
                        className="flex flex-1 items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600"
                      >
                        <MdDirections className="mr-2 h-4 w-4" />
                        Directions
                      </button>
                      <button
                        onClick={() => handleBookAppointment(clinic)}
                        className="linear flex flex-1 items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                      >
                        Book Appointment
                      </button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>

      {/* SMS Access Notice */}
      <div className="mt-6 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
        <div className="flex items-start">
          <div className="mr-3 mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-800">
            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
              📱
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
              <strong>No smartphone?</strong> Text "HELP" to 12345 for SMS-based
              clinic search
            </p>
            <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
              Reply with your location to get clinic details via SMS
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindClinic;
